import { useEffect, useState } from "react";
import { Mail, Package, X, Loader2, BadgeCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const TEMPLATES = [
  {
    label: "Stock shortage",
    eta: "7-10 business days",
    body: "We're so sorry — we ran into a stock shortage on one of your items, so your order is going to take a bit longer than our usual 3-5 business day window. We'll have it on its way to you as soon as possible. Thanks so much for your patience! 💖",
  },
  {
    label: "Material delay",
    eta: "5-7 business days",
    body: "A heads-up that we're running slightly behind because of a material delay. We're working on your order and will ship it as soon as we can. Thank you for your patience! ✨",
  },
  {
    label: "Custom request — extra time",
    eta: "10-14 business days",
    body: "Thanks for your custom order! Because of the extra love it needs, please allow a bit more time than usual. We'll keep you posted and ship it the moment it's ready. 💖",
  },
];

function statusPill(status) {
  const s = (status || "").toLowerCase();
  const cls = s === "paid"
    ? "bg-[#2eb6a8]/15 text-[#1f8d83] border-[#2eb6a8]/40"
    : s === "refunded"
    ? "bg-[#9c6dde]/15 text-[#7a4dc4] border-[#9c6dde]/40"
    : "bg-[#f3c4d8]/30 text-[#c93875] border-[#e8478e]/40";
  return <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${cls}`}>{s}</span>;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delay, setDelay] = useState(null); // selected order id
  const [delayMsg, setDelayMsg] = useState("");
  const [delayEta, setDelayEta] = useState("");
  const [sending, setSending] = useState(false);
  const [taxStatus, setTaxStatus] = useState({}); // email -> bool
  const [busyEmail, setBusyEmail] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchOrders = () => {
    setLoading(true);
    api.get("/orders")
      .then(({ data }) => {
        setOrders(data);
        // Bulk-fetch tax-exempt status for unique emails
        const uniqEmails = [...new Set(data.map((o) => (o.email || "").toLowerCase()).filter(Boolean))];
        Promise.all(uniqEmails.map((e) =>
          api.get(`/admin/customers/${encodeURIComponent(e)}/tax-status`).then(({ data: d }) => [e, !!d.tax_exempt]).catch(() => [e, false])
        )).then((pairs) => {
          setTaxStatus(Object.fromEntries(pairs));
        });
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };
  useEffect(fetchOrders, []);

  const toggleTaxExempt = async (email) => {
    const e = (email || "").toLowerCase();
    if (!e) return;
    const next = !taxStatus[e];
    setBusyEmail(e);
    try {
      await api.put(`/admin/customers/${encodeURIComponent(e)}/tax-status`, { tax_exempt: next });
      setTaxStatus((t) => ({ ...t, [e]: next }));
      toast.success(next ? `${email} is now tax-exempt` : `${email} is no longer tax-exempt`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update");
    } finally {
      setBusyEmail(null);
    }
  };

  const openDelay = (order) => {
    setDelay(order);
    const tpl = TEMPLATES[0];
    setDelayMsg(tpl.body);
    setDelayEta(tpl.eta);
  };
  const closeDelay = () => { setDelay(null); setDelayMsg(""); setDelayEta(""); };

  const sendDelay = async () => {
    if (!delay) return;
    if (!delayMsg.trim()) { toast.error("Please add a message"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${delay.id}/notify-delay`, { message: delayMsg.trim(), new_eta: delayEta.trim() || null });
      if (data.email_sent) {
        toast.success(`Delay email sent to ${delay.email}`);
      } else {
        toast.warning("Saved, but email could not be delivered. Check Resend logs.");
      }
      closeDelay();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    const typed = window.prompt(
      `⚠️ This will permanently delete ALL ${orders.length} order${orders.length === 1 ? "" : "s"}.\n\nThis cannot be undone.\n\nType DELETE to confirm:`,
      "",
    );
    if (typed !== "DELETE") {
      if (typed !== null) toast.error("You must type DELETE exactly to confirm");
      return;
    }
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/orders", { params: { confirm: "YES" } });
      toast.success(`Cleared ${data.deleted} order${data.deleted === 1 ? "" : "s"}. Clean slate ✨`);
      fetchOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to clear");
    } finally {
      setClearing(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-[#8a8076]"><Loader2 className="inline animate-spin mr-2" size={16}/> Loading orders…</div>;

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center gap-3 mb-5">
        <Package className="text-[#e8478e]" size={20}/>
        <h3 className="font-display text-2xl font-black text-[#1c1815]">Orders</h3>
        <span className="text-sm text-[#8a8076]">({orders.length})</span>
        <div className="flex-1"/>
        {orders.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            disabled={clearing}
            data-testid="clear-all-orders"
            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] px-3.5 py-2 rounded-full border border-[#c93875]/40 bg-[#c93875]/8 text-[#c93875] hover:bg-[#c93875] hover:text-white hover:border-[#c93875] transition-all disabled:opacity-50"
          >
            <Trash2 size={11}/> {clearing ? "Clearing…" : "Clear All Orders"}
          </button>
        )}
      </div>

      {orders.length === 0 && (
        <div className="p-10 text-center rounded-2xl border border-dashed border-[#e5dccd] text-[#8a8076]">
          No orders yet.
        </div>
      )}

      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.id} data-testid={`order-row-${o.id}`} className="flex flex-wrap items-center gap-4 p-4 rounded-2xl border border-[#e5dccd] bg-white">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-3">
                <div className="font-mono text-xs text-[#8a8076]">#{o.id.slice(0, 8).toUpperCase()}</div>
                {statusPill(o.status)}
                {o.rush_fee > 0 && <span className="text-[10px] font-bold uppercase tracking-[0.18em] bg-[#e8478e]/15 text-[#c93875] border border-[#e8478e]/40 px-2 py-1 rounded-full">Rush</span>}
              </div>
              <div className="font-bold text-[#1c1815] mt-1">{o.full_name}</div>
              <div className="text-xs text-[#8a8076] flex flex-wrap items-center gap-2">
                <span>{o.email}</span>
                <span>·</span>
                <span>{new Date(o.created_at).toLocaleString()}</span>
                {taxStatus[o.email?.toLowerCase()] && (
                  <span className="inline-flex items-center gap-1 bg-[#9c6dde]/15 border border-[#9c6dde]/40 text-[#7a4dc4] text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full" data-testid={`tax-exempt-badge-${o.id}`}>
                    <BadgeCheck size={10}/> Tax-Exempt
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-[#4a4239]">
                {o.items.map((it, idx) => (
                  <span key={idx}>{idx > 0 && ", "}{it.title}{it.variant_size ? ` (${it.variant_size})` : ""} ×{it.quantity}</span>
                ))}
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-1.5">
              <div className="font-display font-extrabold text-xl text-[#1c1815]">${o.total.toFixed(2)}</div>
              <button
                type="button"
                onClick={() => openDelay(o)}
                data-testid={`notify-delay-${o.id}`}
                className="inline-flex items-center gap-1.5 bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] hover:border-[#e8478e] text-[#1c1815] text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-full transition-colors"
              >
                <Mail size={12}/> Send Delay Email
              </button>
              <button
                type="button"
                onClick={() => toggleTaxExempt(o.email)}
                disabled={busyEmail === (o.email || "").toLowerCase()}
                data-testid={`toggle-tax-exempt-${o.id}`}
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-60 ${
                  taxStatus[o.email?.toLowerCase()]
                    ? "bg-[#9c6dde] hover:bg-[#7a4dc4] text-white border border-[#9c6dde]"
                    : "bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] hover:border-[#9c6dde] text-[#1c1815]"
                }`}
              >
                <BadgeCheck size={12}/>
                {taxStatus[o.email?.toLowerCase()] ? "Tax-Exempt ✓" : "Mark Tax-Exempt"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {delay && (
        <div className="fixed inset-0 z-[80] bg-[#1c1815]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeDelay} data-testid="delay-modal">
          <div className="bg-white rounded-3xl border border-[#e5dccd] max-w-lg w-full p-6 sm:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-script text-2xl text-[#e8478e]">Heads-Up Email</div>
                <h3 className="font-display text-2xl font-black text-[#1c1815] tracking-tight">Order #{delay.id.slice(0, 8).toUpperCase()}</h3>
                <div className="text-xs text-[#8a8076] mt-1">Sending to <strong className="text-[#1c1815]">{delay.email}</strong></div>
              </div>
              <button onClick={closeDelay} className="p-2 rounded-full hover:bg-[#efe5d4]"><X size={18}/></button>
            </div>

            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold mb-2">Quick Templates</div>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATES.map((t) => (
                  <button key={t.label} type="button" onClick={() => { setDelayMsg(t.body); setDelayEta(t.eta); }}
                    data-testid={`template-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] text-[#1c1815] px-3 py-1.5 rounded-full">
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">New Estimated Time (optional)</label>
              <input value={delayEta} onChange={(e) => setDelayEta(e.target.value)} placeholder="e.g. 7-10 business days"
                data-testid="delay-eta"
                className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"/>
            </div>

            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Message</label>
              <textarea rows={6} value={delayMsg} onChange={(e) => setDelayMsg(e.target.value)}
                data-testid="delay-message"
                className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none resize-y text-[#1c1815]"/>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 justify-end">
              <button type="button" onClick={closeDelay} className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8a8076] hover:text-[#1c1815] px-4 py-2.5">Cancel</button>
              <button type="button" onClick={sendDelay} disabled={sending}
                data-testid="delay-send"
                className="inline-flex items-center gap-2 bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full transition-all disabled:opacity-60">
                <Mail size={14}/> {sending ? "Sending…" : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
